import { connectedStairs, stairSpec, stairLanding, nearStairLanding, stairWorld, inStairHole, withStairContext } from './stairs.js';
import { clamp, WALL_THICKNESS } from './constants.js';
import { distToSeg, insideSite, siteBBox, blocked, ptInPoly, wallIntersection } from './geometry.js';
import { sceneWalls } from './siteGeometry.js';

export const EYE_HEIGHT = 1.65;
const BODY_RADIUS = 0.2;

function obstructionClearance(w, x, y) {
  if (w.wallPts) return ptInPoly(x, y, w.wallPts) ? 0 : Math.min(...w.wallPts.map((p, i, pts) => distToSeg(x, y, p.x, p.y, pts[(i + 1) % pts.length].x, pts[(i + 1) % pts.length].y)));
  if(!w.objectKind)return distToSeg(x,y,w.x1,w.y1,w.x2,w.y2)-(w.thickness??WALL_THICKNESS)/2;
  const dx=w.x2-w.x1,dy=w.y2-w.y1,length=Math.hypot(dx,dy);
  if(!length)return Math.hypot(x-w.x1,y-w.y1);
  const px=x-(w.x1+w.x2)/2,py=y-(w.y1+w.y2)/2;
  // Furniture parts are rectangular prisms, not thick lines with round caps.
  // Capsule caps used to add half a desk's depth beyond each physical end.
  return Math.hypot(Math.max(0,Math.abs((px*dx+py*dy)/length)-length/2),Math.max(0,Math.abs((py*dx-px*dy)/length)-(w.thickness??WALL_THICKNESS)/2));
}

export function walkable(S, x, z) {
  return !inStairHole(S, x, z) && sceneWalls(S).every(w => w.h < 0.3 || (w.bottom || 0) > EYE_HEIGHT + 0.15 ||
    obstructionClearance(w,x,z) > BODY_RADIUS);
}

// Build once per document revision, not for every 8 cm collision substep.
// Conservative grid bounds only discard walls that cannot touch the walker;
// the final oriented-footprint test is identical to walkable().
export function walkCollision(S) {
  const cells = new Map(), large = [], size = 3;
  for (const wall of sceneWalls(S)) {
    if (wall.h < .3 || (wall.bottom || 0) > EYE_HEIGHT + .15) continue;
    const pad = (wall.thickness ?? WALL_THICKNESS) / 2 + BODY_RADIUS;
    const x0 = Math.floor((Math.min(wall.x1, wall.x2) - pad) / size), x1 = Math.floor((Math.max(wall.x1, wall.x2) + pad) / size);
    const y0 = Math.floor((Math.min(wall.y1, wall.y2) - pad) / size), y1 = Math.floor((Math.max(wall.y1, wall.y2) + pad) / size);
    if ((x1 - x0 + 1) * (y1 - y0 + 1) > 4096) { large.push(wall); continue; }
    for (let x = x0; x <= x1; x++) for (let y = y0; y <= y1; y++) {
      const key = `${x},${y}`, bucket = cells.get(key) || []; bucket.push(wall); cells.set(key, bucket);
    }
  }
  return (x, z) => !inStairHole(S, x, z) && [...large, ...(cells.get(`${Math.floor(x / size)},${Math.floor(z / size)}`) || [])].every(w => obstructionClearance(w, x, z) > BODY_RADIUS);
}

// Check the walking body along the flight against each intersected level's
// physical obstructions. This prevents a landing action bypassing a wall/roof.
function stairFlightIssue(S, stair, c) {
  const levels = [{ id: S.activeFloorId, name: S.floorName, elevation: S.elevation || 0, plan: S }, ...(S.floors || [])];
  for (const f of levels) {
    if (f.elevation < c.lower.elevation || f.elevation > c.upper.elevation) continue;
    const walls = sceneWalls(withStairContext(S, f)).filter(w => w.sourceId !== stair.id);
    for (let i = 0; i < c.count; i++) for (const side of [-1, 0, 1]) {
      const p = stairWorld(stair, -stair.run / 2 + (i + 0.5) * c.tread, side * Math.max(0, stair.width / 2 - 0.1));
      const h = c.lower.elevation - f.elevation + (i + 1) * c.riser;
      if (blocked(p.x, p.y, h + 0.05, p.x, p.y, h + EYE_HEIGHT + 0.15, walls)) return { message: `The stair flight meets an obstruction on ${f.name}. Move it or adjust walls, objects or ceilings.`, floorId: f.id, point: p };
    }
  }
  return '';
}

/** Validate both arrival positions, and offer travel only at a landing. */
export function stairRoutes(S, position = null) {
  return connectedStairs(S).flatMap(stair => {
    const c = stairSpec(S, stair); if (!c) return [];
    const down = S.activeFloorId === stair.toFloorId, destination = down ? c.lower : c.upper;
    const source = stairLanding(stair, down), arrival = stairLanding(stair, !down);
    const level = S.floors?.find(f => f.id === destination.id);
    if (!level) return [];
    const plan = withStairContext(S, level);
    let error = '';
    if (!insideSite(S, source.x, source.y)) error = 'The departure landing must be inside a drawn floor or exterior site area.';
    else if (!walkable(S, source.x, source.y)) error = 'The departure landing is obstructed. Move the stairs or clear the landing.';
    else if (!insideSite(plan, arrival.x, arrival.y)) error = `Draw a floor or exterior site area beneath the landing on ${destination.name}.`;
    else if (!walkable(plan, arrival.x, arrival.y)) error = `The landing on ${destination.name} is obstructed. Move the stairs or clear the landing.`;
    const flight = !error ? stairFlightIssue(S, stair, c) : null;
    const issue = flight || (error ? { message: error, floorId: error.includes('departure') ? S.activeFloorId : destination.id, point: error.includes('departure') ? source : arrival } : null);
    if (flight) error = flight.message;
    const near = nearStairLanding(stair, down, position);
    return [{ stair, destination, source, arrival, down, near, error, issue }];
  });
}

export function walkStart(S) {
  const b = siteBBox(S);
  if (!b) return { x: 0, z: 0 };
  const cx = (b.x0 + b.x1) / 2, cz = (b.y0 + b.y1) / 2;
  if (insideSite(S, cx, cz) && walkable(S, cx, cz)) return { x: cx, z: cz };
  const choices = [];
  for (const a of S.areas.filter(a => a.kind === 'floor')) {
    const xs = a.pts.map(p => p.x), ys = a.pts.map(p => p.y);
    for (let i = 0; i < 20; i++) for (let j = 0; j < 20; j++) {
      const x = Math.min(...xs) + (Math.max(...xs) - Math.min(...xs)) * (i + 0.5) / 20;
      const z = Math.min(...ys) + (Math.max(...ys) - Math.min(...ys)) * (j + 0.5) / 20;
      if (insideSite(S, x, z) && walkable(S, x, z)) choices.push({ x, z });
    }
  }
  choices.sort((a, c) => Math.hypot(a.x - cx, a.z - cz) - Math.hypot(c.x - cx, c.z - cz));
  return choices[0] || { x: b.x0 - 1, z: b.y0 - 1 };
}

/** A clear indoor arrival looking across usable space. Bounded sampling keeps
 * entering Walk responsive; saved views and stair arrivals bypass this choice. */
export function walkView(S) {
  const walls = sceneWalls(S), choices = [];
  const areas = (S.areas || []).filter(a => a.kind === 'floor' && a.environment !== 'outdoor');
  for (const a of areas) {
    const xs = a.pts.map(p => p.x), ys = a.pts.map(p => p.y);
    const nx = Math.max(2, Math.min(8, Math.floor(Math.sqrt(256 / Math.max(1, areas.length)))));
    for (let i = 0; i < nx; i++) for (let j = 0; j < nx; j++) {
      const x = Math.min(...xs) + (Math.max(...xs) - Math.min(...xs)) * (i + .5) / nx;
      const z = Math.min(...ys) + (Math.max(...ys) - Math.min(...ys)) * (j + .5) / nx;
      if (!ptInPoly(x, z, a.pts) || !insideSite(S, x, z) || inStairHole(S, x, z)) continue;
      if (walls.some(w => wallIntersection(x, z, .15, x, z, EYE_HEIGHT + .15, w) ||
        (!w.roofPts && w.h >= .3 && (w.bottom || 0) <= EYE_HEIGHT + .15 && distToSeg(x, z, w.x1, w.y1, w.x2, w.y2) < .45 + (w.thickness ?? WALL_THICKNESS) / 2))) continue;
      choices.push({ x, z });
    }
  }
  let best = null, score = -1;
  for (const p of choices) {
    const distances = Array.from({ length: 24 }, (_, i) => {
      const a = i * Math.PI / 12, x = p.x + 12 * Math.cos(a), z = p.z + 12 * Math.sin(a);
      return walls.reduce((d, w) => { const hit = wallIntersection(p.x, p.z, EYE_HEIGHT, x, z, EYE_HEIGHT, w); return hit == null ? d : Math.min(d, hit.enter * 12); }, 12);
    });
    distances.forEach((d, i) => {
      const value = d + .6 * (distances[(i + 2) % 24] + distances[(i + 22) % 24]);
      if (value > score) { score = value; const a = i * Math.PI / 12; best = { ...p, yaw: Math.atan2(-Math.cos(a), -Math.sin(a)), pitch: -.07 }; }
    });
  }
  return best || { ...walkStart(S), yaw: -Math.PI / 2, pitch: 0 };
}

/** Horizontal movement with small collision steps and sliding along walls.
 * The site boundary is not a fence: users can walk outside through openings. */
export function moveWalk(S, position, dx, dz, clear = walkCollision(S)) {
  const steps = Math.max(1, Math.ceil(Math.hypot(dx, dz) / 0.08));
  for (let i = 0; i < steps; i++) {
    const x = position.x + dx / steps, z = position.z + dz / steps;
    if (clear(x, z)) { position.x = x; position.z = z; }
    else {
      if (clear(x, position.z)) position.x = x;
      if (clear(position.x, z)) position.z = z;
    }
  }
  position.y = EYE_HEIGHT;
}

export class WalkControls {
  constructor(camera, canvas, getDoc, getRevision) {
    this.camera = camera; this.canvas = canvas; this.getDoc = getDoc;
    this.enabled = false; this.keys = new Set(); this.drag = null;
    this.collision = () => {
      const doc = getDoc(), revision = getRevision?.();
      if (!getRevision || this.collisionDoc !== doc || this.collisionRevision !== revision) {
        this.collisionDoc = doc; this.collisionRevision = revision; this.clear = walkCollision(doc);
      }
      return this.clear;
    };
    this.yaw = -Math.PI / 2; this.pitch = 0;
    const movement = new Set(['KeyW', 'KeyA', 'KeyS', 'KeyD', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ShiftLeft', 'ShiftRight']);
    this.handlers = {
      pointerdown: e => {
        if (!this.enabled || e.button !== 0) return;
        canvas.focus({ preventScroll: true });
        this.drag = { id: e.pointerId, x: e.clientX, y: e.clientY };
        canvas.setPointerCapture(e.pointerId); e.preventDefault();
      },
      pointermove: e => {
        if (!this.enabled || this.drag?.id !== e.pointerId) return;
        this.yaw -= (e.clientX - this.drag.x) * 0.004;
        this.pitch = clamp(this.pitch - (e.clientY - this.drag.y) * 0.004, -Math.PI / 2 + 0.05, Math.PI / 2 - 0.05);
        this.drag.x = e.clientX; this.drag.y = e.clientY;
        this.orient();
      },
      pointerup: () => { this.drag = null; },
      pointercancel: () => this.stop(),
      keydown: e => {
        if (!this.enabled || e.ctrlKey || e.metaKey || e.altKey || !movement.has(e.code)) return;
        this.keys.add(e.code); e.preventDefault(); e.stopPropagation();
      },
      wheel: e => {
        if (!this.enabled) return;
        e.preventDefault();
        const d = -Math.sign(e.deltaY) * 0.6;
        moveWalk(getDoc(), camera.position, -Math.sin(this.yaw) * d, -Math.cos(this.yaw) * d, this.collision());
      },
      blur: () => this.stop()
    };
    for (const [event, handler] of Object.entries(this.handlers)) canvas.addEventListener(event, handler, { passive: false });
    this.keyup = e => this.keys.delete(e.code);
    this.blur = () => this.stop();
    this.visibility = () => { if (document.hidden) this.stop(); };
    window.addEventListener('keyup', this.keyup);
    window.addEventListener('blur', this.blur);
    document.addEventListener('visibilitychange', this.visibility);
  }
  orient() { this.camera.rotation.set(this.pitch, this.yaw, 0, 'YXZ'); }
  reset() {
    const p = walkView(this.getDoc());
    this.camera.position.set(p.x, EYE_HEIGHT, p.z);
    this.yaw = p.yaw; this.pitch = p.pitch; this.orient(); this.stop();
  }
  stop() { this.keys.clear(); this.drag = null; }
  update(dt) {
    if (!this.enabled) return;
    const has = (...codes) => codes.some(k => this.keys.has(k)) ? 1 : 0;
    let forward = has('KeyW', 'ArrowUp') - has('KeyS', 'ArrowDown');
    let right = has('KeyD', 'ArrowRight') - has('KeyA', 'ArrowLeft');
    const len = Math.hypot(forward, right);
    if (!len) return;
    const distance = Math.min(dt, 0.1) * (has('ShiftLeft', 'ShiftRight') ? 6 : 2.4) / len;
    forward *= distance; right *= distance;
    moveWalk(this.getDoc(), this.camera.position,
      -Math.sin(this.yaw) * forward + Math.cos(this.yaw) * right,
      -Math.cos(this.yaw) * forward - Math.sin(this.yaw) * right, this.collision());
  }
  dispose() {
    this.stop();
    for (const [event, handler] of Object.entries(this.handlers)) this.canvas.removeEventListener(event, handler);
    window.removeEventListener('keyup', this.keyup); window.removeEventListener('blur', this.blur);
    document.removeEventListener('visibilitychange', this.visibility);
  }
}
